import React, { useState, useRef } from 'react';
import {
  loadSettings, saveSettings,
  playNewOrderSound, playUrgentOrderSound, playPaymentSound, playOverdueSound
} from './SoundSystem';
import {
  Volume2, VolumeX, Bell, Upload, Play, Save, CheckCircle,
  ShoppingBag, X as XIcon, CreditCard, Star
} from 'lucide-react';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';

const CUSTOM_KEY = 'lunora_custom_sounds'; // { newOrder: dataURL, cancel: dataURL, ... }

const loadCustomSounds = () => {
  try { return JSON.parse(localStorage.getItem(CUSTOM_KEY)) || {}; } catch { return {}; }
};
const saveCustomSounds = (v) => { try { localStorage.setItem(CUSTOM_KEY, JSON.stringify(v)); } catch {} };

const SOUNDS = [
  { id: 'newOrder', label: 'New Order', icon: ShoppingBag, play: playNewOrderSound, desc: 'When a customer places a new order' },
  { id: 'cancel', label: 'Cancel Order', icon: XIcon, play: playOverdueSound, desc: 'When an order is cancelled' },
  { id: 'payment', label: 'Payment Received', icon: CreditCard, play: playPaymentSound, desc: 'When payment is credited' },
  { id: 'vip', label: 'VIP Order', icon: Star, play: playUrgentOrderSound, desc: 'Priority / urgent / VIP orders' },
];

const AdminSoundSettings = () => {
  const [settings, setSettings] = useState(loadSettings());
  const [custom, setCustom] = useState(loadCustomSounds());
  const [saved, setSaved] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const fileRefs = useRef({});

  const update = (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
  };

  const handleSave = () => {
    saveSettings(settings);
    saveCustomSounds(custom);
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  const playSoundById = (s) => {
    setTestingId(s.id);
    const customDataURL = custom[s.id];
    if (customDataURL) {
      const audio = new Audio(customDataURL);
      audio.volume = settings.volume;
      audio.play().catch(() => {});
      audio.onended = () => setTestingId(null);
      setTimeout(() => setTestingId(null), 3000);
    } else {
      s.play(settings.volume);
      setTimeout(() => setTestingId(null), 1000);
    }
  };

  const handleFileUpload = (soundId, file) => {
    if (!file || !file.type.startsWith('audio/')) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Audio file must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setCustom(prev => ({ ...prev, [soundId]: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const clearCustom = (soundId) => {
    setCustom(prev => {
      const next = { ...prev };
      delete next[soundId];
      return next;
    });
  };

  return (
    <div data-testid="admin-sound-settings" className="pb-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-[16px]" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>Notification Sounds</h3>
          <p className="text-[10px]" style={{ color: MUTED }}>Premium Web Audio tones — crystal clear, non-intrusive</p>
        </div>
        <button data-testid="sound-master-toggle" onClick={() => update({ soundEnabled: !settings.soundEnabled })}
          className="w-10 h-10 flex items-center justify-center"
          style={{ backgroundColor: settings.soundEnabled ? GOLD : '#FFF', border: `1px solid ${settings.soundEnabled ? GOLD : LINE}` }}>
          {settings.soundEnabled
            ? <Volume2 className="w-4 h-4 text-white" strokeWidth={1.5} />
            : <VolumeX className="w-4 h-4" style={{ color: MUTED }} strokeWidth={1.5} />}
        </button>
      </div>

      {/* Master controls */}
      <div className="bg-white border p-3 mb-3" style={{ borderColor: LINE }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px]" style={{ color: INK, fontWeight: 500 }}>Master Sound</span>
          <span className="text-[10px]" style={{ color: settings.soundEnabled ? '#166534' : '#991B1B' }}>
            {settings.soundEnabled ? 'ON' : 'OFF'}
          </span>
        </div>
        <div>
          <label className="text-[9px] tracking-[0.1em] uppercase" style={{ color: MUTED }}>
            Volume — {Math.round(settings.volume * 100)}%
          </label>
          <input data-testid="volume-slider" type="range" min="0" max="1" step="0.05" value={settings.volume}
            onChange={e => update({ volume: parseFloat(e.target.value) })}
            className="w-full mt-1 accent-[#D4AF37]"
            disabled={!settings.soundEnabled} />
        </div>
      </div>

      {/* Per-sound controls */}
      <div className="space-y-2 mb-4">
        {SOUNDS.map(s => {
          const hasCustom = !!custom[s.id];
          const Icon = s.icon;
          return (
            <div key={s.id} data-testid={`sound-row-${s.id}`} className="bg-white border p-3" style={{ borderColor: LINE }}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(212,175,55,0.08)' }}>
                  <Icon className="w-4 h-4" style={{ color: GOLD }} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px]" style={{ color: INK, fontWeight: 500 }}>{s.label}</p>
                  <p className="text-[10px]" style={{ color: MUTED }}>{s.desc}</p>
                  {hasCustom && (
                    <span className="inline-block mt-1 text-[9px] px-1.5 py-0.5" style={{ backgroundColor: 'rgba(212,175,55,0.1)', color: GOLD }}>
                      Custom sound active
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1.5 mt-2">
                <button data-testid={`sound-test-${s.id}`}
                  onClick={() => playSoundById(s)}
                  disabled={!settings.soundEnabled}
                  className="flex-1 py-2 text-[10px] tracking-[0.06em] uppercase flex items-center justify-center gap-1 border disabled:opacity-40"
                  style={{ borderColor: testingId === s.id ? GOLD : LINE, color: testingId === s.id ? GOLD : INK }}>
                  <Play className="w-3 h-3" strokeWidth={1.5} />
                  {testingId === s.id ? 'Playing...' : 'Test'}
                </button>
                <label data-testid={`sound-upload-label-${s.id}`}
                  className="flex-1 py-2 text-[10px] tracking-[0.06em] uppercase flex items-center justify-center gap-1 border cursor-pointer"
                  style={{ borderColor: LINE, color: INK }}>
                  <Upload className="w-3 h-3" strokeWidth={1.5} />
                  Custom
                  <input ref={el => (fileRefs.current[s.id] = el)}
                    data-testid={`sound-upload-input-${s.id}`}
                    type="file" accept="audio/*" className="hidden"
                    onChange={e => handleFileUpload(s.id, e.target.files?.[0])} />
                </label>
                {hasCustom && (
                  <button data-testid={`sound-clear-${s.id}`}
                    onClick={() => clearCustom(s.id)}
                    className="py-2 px-3 text-[10px] tracking-[0.06em] uppercase border"
                    style={{ borderColor: '#991B1B', color: '#991B1B' }}>
                    Reset
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Save */}
      <button data-testid="sound-save-btn" onClick={handleSave}
        className="w-full py-3 text-[11px] tracking-[0.1em] uppercase text-white flex items-center justify-center gap-2"
        style={{ backgroundColor: saved ? '#166534' : INK }}>
        {saved ? <><CheckCircle className="w-4 h-4" strokeWidth={1.5} /> Saved</> : <><Save className="w-4 h-4" strokeWidth={1.5} /> Save Sound Settings</>}
      </button>

      <p className="text-[9px] text-center mt-2" style={{ color: '#BFBFBF' }}>
        Custom sounds are stored locally on this device (max 2MB each). Premium tones fallback if custom unavailable.
      </p>
    </div>
  );
};

export default AdminSoundSettings;
